/* RESPONSIBLE TEAM: team-ai-agent */
import type IntlService from 'ember-intl/services/intl';
import { type LogicalFilter } from 'embercom/components/reporting/custom/filters';

function and(...filters: any[]): LogicalFilter {
  let compactFilters = filters.filter(Boolean);
  if (compactFilters.length === 1) {
    return compactFilters[0];
  }
  return {
    type: 'and',
    filters: compactFilters.flatMap((f: any) => (f.type === 'and' ? f.filters : f)),
  };
}

function wrap(filters: any) {
  if (filters && filters.type !== 'and' && filters.type !== 'or') {
    return { type: 'and', filters: [filters] };
  }
  return filters;
}

function funnelSeries(filters?: any, label?: string) {
  return {
    label,
    metric_id: 'v1.new_conversations',
    filters: wrap(filters),
  };
}

function config(name: string, filters?: any) {
  return { name, series: funnelSeries(filters, name) };
}

const ecomFilters = {
  get productRecommended() {
    return {
      type: 'category' as const,
      data: {
        property: 'conversation_custom_fields#ecommerce_agent_carousel_served',
        attribute: 'conversation_custom_fields#ecommerce_agent_carousel_served',
        values: [true],
      },
    };
  },
  get productClicked() {
    return {
      type: 'category' as const,
      data: {
        property: 'conversation_custom_fields#product_card_clicked',
        attribute: 'conversation_custom_fields#product_card_clicked',
        values: [true],
      },
    };
  },
  get cartCreated() {
    return {
      type: 'category' as const,
      data: {
        property: 'conversation_custom_fields#cart_created',
        attribute: 'conversation_custom_fields#cart_created',
        values: [true],
      },
    };
  },
  get checkoutClicked() {
    return {
      type: 'category' as const,
      data: {
        property: 'conversation_custom_fields#checkout_link_clicked',
        attribute: 'conversation_custom_fields#checkout_link_clicked',
        values: [true],
      },
    };
  },
};

export interface EcommerceFunnelMetrics {
  ecomInvolved: number;
  productRecommended: number;
  productRecommendedAndClicked: number;
  productRecommendedAndCartCreated: number;
  productClickedAndCartCreated: number;
  cartCreatedAndCheckout: number;
  productClicked: number;
  cartCreated: number;
  checkoutClicked: number;
}

function buildEcommerceFunnelSeriesFilters(participatedFilter: any) {
  let f = ecomFilters;
  return [
    config('ecomInvolved', participatedFilter),
    config('productRecommended', and(participatedFilter, f.productRecommended)),
    config(
      'productRecommendedAndClicked',
      and(participatedFilter, f.productRecommended, f.productClicked),
    ),
    config(
      'productRecommendedAndCartCreated',
      and(participatedFilter, f.productRecommended, f.cartCreated),
    ),
    config(
      'productClickedAndCartCreated',
      and(participatedFilter, f.productClicked, f.cartCreated),
    ),
    config('cartCreatedAndCheckout', and(participatedFilter, f.cartCreated, f.checkoutClicked)),
    config('productClicked', and(participatedFilter, f.productClicked)),
    config('cartCreated', and(participatedFilter, f.cartCreated)),
    config('checkoutClicked', and(participatedFilter, f.checkoutClicked)),
  ];
}

const FUNNEL_METRIC_LABELS: Record<string, string> = {
  ecomInvolved: 'ai-agent.analyze.ecommerce-performance.funnel-metrics.ecomInvolved',
  productRecommended: 'ai-agent.analyze.ecommerce-performance.funnel-metrics.productRecommended',
  productRecommendedAndClicked:
    'ai-agent.analyze.ecommerce-performance.funnel-metrics.productRecommendedAndClicked',
  productRecommendedAndCartCreated:
    'ai-agent.analyze.ecommerce-performance.funnel-metrics.productRecommendedAndCartCreated',
  productClickedAndCartCreated:
    'ai-agent.analyze.ecommerce-performance.funnel-metrics.productClickedAndCartCreated',
  cartCreatedAndCheckout:
    'ai-agent.analyze.ecommerce-performance.funnel-metrics.cartCreatedAndCheckout',
  productClicked: 'ai-agent.analyze.ecommerce-performance.funnel-metrics.productClicked',
  cartCreated: 'ai-agent.analyze.ecommerce-performance.funnel-metrics.cartCreated',
  checkoutClicked: 'ai-agent.analyze.ecommerce-performance.funnel-metrics.checkoutClicked',
};

export function buildEcommerceFunnelConfig(participatedFilter: any) {
  return {
    chartSeriesFilters() {
      return buildEcommerceFunnelSeriesFilters(participatedFilter);
    },

    chartSeries() {
      return this.chartSeriesFilters().map((c) => c.series);
    },

    getChartSeriesWithLabels(intl: IntlService) {
      return this.chartSeriesFilters().map((c) => ({
        ...c.series,
        label: intl.t(FUNNEL_METRIC_LABELS[c.name]),
      }));
    },

    getMetricsFromChartDataRequest(chartData: any): EcommerceFunnelMetrics {
      let result = {} as EcommerceFunnelMetrics;
      let seriesFilters = this.chartSeriesFilters();
      for (let i = 0; i < chartData.length; i++) {
        let seriesName = seriesFilters[i].name as keyof EcommerceFunnelMetrics;
        let seriesData = chartData[i].data[0];
        result[seriesName] = seriesData;
      }
      return result;
    },
  };
}

export const DEMO_METRICS: EcommerceFunnelMetrics = {
  ecomInvolved: 3200,
  productRecommended: 1842,
  productRecommendedAndClicked: 1105,
  productRecommendedAndCartCreated: 368,
  productClickedAndCartCreated: 737,
  cartCreatedAndCheckout: 553,
  productClicked: 1105,
  cartCreated: 1105,
  checkoutClicked: 553,
};
